// The app store: Zustand slices composed into one store. Server data does NOT
// live here — that is TanStack Query's job. This holds identity, station,
// in-flight job UI, and device settings.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { todayKey } from '@/lib/milestone';
import { createSettingsSlice, type SettingsSlice } from './slices/settings';
import { createUiSlice, type UiSlice } from './slices/ui';
import { createSessionSlice, type SessionSlice } from './slices/session';
import { createJobSlice, type JobSlice } from './slices/job';
import { createAndonSlice, type AndonSlice } from './slices/andon';

export type AppStore = SettingsSlice & UiSlice & SessionSlice & JobSlice & AndonSlice;

/** Keys that survive a reload (device-level preferences only). */
type PersistedSettings = Pick<
  SettingsSlice,
  | 'userTheme'
  | 'userAccent'
  | 'language'
  | 'milestoneShownOn'
  | 'speechConsent'
  | 'installDismissedOn'
  | 'leaderDepartmentId'
  | 'iswRecent'
>;

export const useStore = create<AppStore>()(
  persist(
    (...a) => ({
      ...createSettingsSlice(...a),
      ...createUiSlice(...a),
      ...createSessionSlice(...a),
      ...createJobSlice(...a),
      // Nothing from it is persisted — see the slice for why a raise draft
      // must not outlive the page.
      ...createAndonSlice(...a),
    }),
    {
      name: 'sf.settings.v1',
      storage: createJSONStorage(() => localStorage),
      // Bumped when the install dismissal became a date instead of a flag.
      version: 1,
      migrate: (persisted, from) => {
        const s = (persisted ?? {}) as Record<string, unknown> & { installDismissedOn?: string | null };
        // Zustand's default version is 0, so that is what the previous
        // release wrote. The `?? 0` is belt and braces for a payload with no
        // version at all: `undefined < 1` is false, which would skip exactly
        // the devices that need migrating.
        if ((from ?? 0) < 1) {
          // Someone who had already dismissed should not be asked again the
          // moment they update — treat the old flag as "dismissed today", so
          // the new 30-day clock starts now rather than in the past.
          s.installDismissedOn = s['installDismissed'] === true ? todayKey() : null;
          delete s['installDismissed'];
        }
        return s;
      },
      partialize: (s): PersistedSettings => ({
        iswRecent: s.iswRecent,
        userTheme: s.userTheme,
        userAccent: s.userAccent,
        language: s.language,
        milestoneShownOn: s.milestoneShownOn,
        speechConsent: s.speechConsent,
        installDismissedOn: s.installDismissedOn,
        leaderDepartmentId: s.leaderDepartmentId,
      }),
    },
  ),
);
